import { CalendarCheck, ChartLine, FileText, MailPlus, MapPinHouse, MessageCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Button } from '@/components/ui/Button/Button';
import { useTimeFor } from '@/hooks/useTimeFor';
import { captureAnalyticsEvent } from '@/lib/mixpanel';
import type { NotificationItem, NotificationType } from '@/types/notification';
import { cn } from '@/utils';
import { CENTER_DAY_KEY_FORMAT } from '@/utils/timezone';
import { useQueryClient } from '@tanstack/react-query';
import { GET_NOTIFICATIONS_INFINITE_QUERY_KEY } from '../../api/getNotifications';
import { useMarkNotificationAsRead } from '../../api/markNotificationAsRead';

const notificationIconMap: Record<NotificationType, React.ElementType> = {
  center_updates: MapPinHouse,
  invoices: FileText,
  enrollment_agreements: FileText,
  reports: ChartLine,
  scheduling_updates: CalendarCheck,
  direct: MailPlus,
};

const mapNotificationToIcon = (notification: NotificationItem) => {
  if (notification.categoryType === 'manual') {
    return MessageCircle;
  }

  if (notification.type in notificationIconMap) {
    return notificationIconMap[notification.type];
  }

  return MailPlus; // Fallback icon
};

interface NotificationListItemProps {
  notification: NotificationItem;
  closePopover?: () => void;
}

export const NotificationListItem = ({ notification, closePopover }: NotificationListItemProps) => {
  const { t } = useTranslation('notifications', { keyPrefix: 'notificationListItem' });
  const { format, formatDate } = useTimeFor('notification');

  const queryClient = useQueryClient();

  const textRef = useRef<HTMLParagraphElement>(null);
  const [isClamped, setIsClamped] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    setIsClamped(el.scrollHeight > el.clientHeight);
  }, [notification.content]);

  const Icon = mapNotificationToIcon(notification);

  const { mutate } = useMarkNotificationAsRead({
    id: notification.id,
    options: {
      onSuccess: () => {
        void queryClient.refetchQueries({ queryKey: [GET_NOTIFICATIONS_INFINITE_QUERY_KEY] });
      },
    },
  });

  const handleOnClick = () => {
    void captureAnalyticsEvent('notifications_opened', { notificationId: notification.id, type: notification.type });
    closePopover?.();
    mutate();
  };

  const redirectUrl: string = notification.redirectUrl?.replace('{notificationId}', notification.id.toString()) ?? '';

  return (
    <li>
      <Link to={redirectUrl} className='border-outline-neutral flex w-full gap-3 border-b px-2 pt-4 pb-8' onClick={handleOnClick}>
        <div className='bg-fill-background-light flex size-11 items-center justify-center rounded-lg'>
          <Icon className='size-5' />
        </div>
        <div className='relative flex w-full flex-1 flex-col gap-3'>
          {!notification.isRead && (
            <>
              <div className='bg-text-brand-primary absolute top-2 right-0 size-2 rounded-full' data-testid='unread-indicator' />
              <span className='sr-only'>{t('unreadAriaLabel')}</span>
            </>
          )}
          <div className='flex flex-col gap-1'>
            <p
              className={cn({
                'text-body-m-bold': !notification.isRead,
              })}
            >
              {notification.title}
            </p>
            <time dateTime={format(notification.createdAt, CENTER_DAY_KEY_FORMAT)} className='text-body-s-regular text-text-default-secondary'>
              {formatDate(notification.createdAt)}
            </time>
          </div>
          {notification.categoryType === 'automatic' ? (
            <div className='text-text-default-secondary'>{notification.content}</div>
          ) : (
            <div className='border-outline-neutral text-text-default-secondary rounded-lg border px-3 py-2'>
              <p ref={textRef} className='line-clamp-3 overflow-hidden'>
                {notification.content}
              </p>

              {isClamped && (
                <Button variant='link' className='text-text-default-secondary py-1 pl-0'>
                  {t('readMore')}
                </Button>
              )}
            </div>
          )}
        </div>
      </Link>
    </li>
  );
};
